import { Component } from '@theme/component';
import { interpolate, prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} HeroSliderRefs
 * @property {HTMLElement[]} slides
 * @property {HTMLButtonElement} [toggle]
 * @property {HTMLElement} [toggleLabel]
 * @property {HTMLElement} [status]
 */

/** Minimum horizontal travel (px) for a touch swipe */
const SWIPE_THRESHOLD = 40;

/**
 * Hero slider: fades between slides, autoplays with a progress ring (`is-playing`), pauses on
 * mouse hover, focus, when off screen or when the tab is hidden; touch swipe; plays the active slide's video.
 * Config: `data-autoplay="true|false"`, `data-interval` (seconds).
 * @extends {Component<HeroSliderRefs>}
 */
class HeroSlider extends Component {
  requiredRefs = ['slides'];

  #index = 0;
  #playing = false;
  /** Paused by hover, focus, visibility */
  #held = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {number | null} */
  #swipeStart = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;

  get #slides() {
    return this.refs.slides || [];
  }

  get #enabled() {
    return this.#slides.length > 1;
  }

  get #interval() {
    return (Number(this.dataset.interval) || 6) * 1000;
  }

  connectedCallback() {
    super.connectedCallback();
    // Theme editor: selecting a slide block shows it and pauses autoplay
    this.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    if (!this.#enabled) {
      for (const slide of this.#slides) slide.classList.add('is-active');
      return;
    }

    this.#playing = this.dataset.autoplay === 'true' && !prefersReducedMotion();
    this.#held = false;

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          if (entry.isIntersecting) {
            this.#held = false;
            this.#schedule();
          } else {
            this.#held = true;
            clearTimeout(this.#timer);
            this.classList.remove('is-playing');
          }
        },
        { threshold: 0.25 }
      );
      this.#observer.observe(this);
    }
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.#syncToggle();
    this.#go(0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
    this.#observer?.disconnect();
    this.#observer = null;
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
  }

  previous() {
    if (this.#enabled) this.#go(this.#index - 1);
  }

  next() {
    if (this.#enabled) this.#go(this.#index + 1);
  }

  togglePlayback() {
    if (!this.#enabled) return;
    this.#playing = !this.#playing;
    this.#syncToggle();
    this.#schedule();
  }

  /** @param {PointerEvent} event */
  onPointerEnter(event) {
    if (event.pointerType === 'mouse') this.#hold(true);
  }

  /** @param {PointerEvent} event */
  onPointerLeave(event) {
    if (event.pointerType === 'mouse') this.#hold(false);
  }

  onFocusIn() {
    this.#hold(true);
  }

  /** @param {FocusEvent} event */
  onFocusOut(event) {
    if (!(event.relatedTarget instanceof Node && this.contains(event.relatedTarget))) this.#hold(false);
  }

  /** @param {PointerEvent} event */
  onPointerDown(event) {
    if (event.pointerType !== 'mouse') this.#swipeStart = event.clientX;
  }

  /** @param {PointerEvent} event */
  onPointerUp(event) {
    if (this.#swipeStart === null) return;
    const distance = event.clientX - this.#swipeStart;
    this.#swipeStart = null;
    if (this.#enabled && Math.abs(distance) > SWIPE_THRESHOLD) this.#go(this.#index + (distance < 0 ? 1 : -1));
  }

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    if (!this.#enabled) return;
    const index = this.#slides.findIndex((slide) => event.target instanceof Node && slide.contains(event.target));
    if (index < 0) return;
    this.#held = true;
    this.#go(index);
  };

  #onBlockDeselect = () => {
    this.#hold(false);
  };

  #onVisibilityChange = () => {
    this.#held = document.hidden;
    this.#schedule();
  };

  /** @param {boolean} held */
  #hold(held) {
    if (!this.#enabled) return;
    this.#held = held;
    this.#schedule();
  }

  /** @param {number} index */
  #go(index) {
    const slides = this.#slides;
    this.#index = (index + slides.length) % slides.length;
    slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.inert = !active;
    });
    const { status } = this.refs;
    if (status) {
      status.textContent = interpolate(window.Theme.translations.slideStatus, {
        index: this.#index + 1,
        total: slides.length,
      });
    }
    this.#syncVideo();
    this.#schedule();
  }

  #syncVideo() {
    this.#slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === this.#index) video.play().catch(() => {});
      else video.pause();
    });
  }

  /** Restarts the progress ring and the autoplay timer */
  #schedule() {
    clearTimeout(this.#timer);
    this.classList.remove('is-playing');
    void this.offsetWidth;
    if (!this.#playing || this.#held) return;
    this.classList.add('is-playing');
    this.#timer = setTimeout(() => this.#go(this.#index + 1), this.#interval);
  }

  #syncToggle() {
    const { toggle, toggleLabel } = this.refs;
    const { pause, play } = window.Theme.translations;
    toggle?.setAttribute('aria-pressed', String(!this.#playing));
    if (toggleLabel) toggleLabel.textContent = this.#playing ? pause : play;
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}
